import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { GraduationCap } from 'lucide-react'
import FormPage from '../../components/FormPage'
import { FormCols, FormSection, InfoNote, SwitchField, Textarea } from '../../components/Form'
import FormRow from '../../components/FormRow'
import Select from '../../components/Select'
import TextField from '../../components/TextField'
import PageLoader from '../../components/PageLoader'
import { useToast } from '../../components/toastContext'
import { getProgram, saveProgram } from './programsService'
import { strings as S } from './strings'
import { serverText } from '../../lib/i18n'

const EMPTY = {
  arabicName: '',
  arabicDescription: '',
  englishName: '',
  englishDescription: '',
  pastCoursesView: 'show',
  futureCoursesView: 'show',
  allowAddingCoursesAfterExamination: false,
}

const asOptions = (map) => Object.entries(map).map(([value, label]) => ({ value, label }))

/**
 * برنامج تدريبي جديد / تعديل — Views/ProgramsManagement/_ProgramManagement_Program_Modal.
 *
 * The legacy's fields, unchanged: two names, two descriptions, the two
 * course-visibility rules, and the one switch that changes what happens to
 * people who have already finished.
 *
 * What changed is the container. This was a 560px side sheet with all seven
 * controls in one column; it is a page now, and the two questions it actually
 * asks — what the programme IS, and what a trainee sees of it — are two steps
 * rather than two headings you scroll past. The legacy stacks its visibility
 * selects under the text fields with nothing marking them as a different kind
 * of decision at all.
 */
export default function ProgramFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const [values, setValues] = useState(EMPTY)
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(!!id)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!id) return
    let alive = true
    getProgram(id)
      .then((program) => {
        if (!alive) return
        setValues({ ...EMPTY, ...program })
        setLoading(false)
      })
      .catch(() => {
        /* Without this a failed load left the form spinning for ever. */
        if (!alive) return
        setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [id])

  const set = (key) => (value) => {
    setValues((previous) => ({ ...previous, [key]: value }))
    setErrors((previous) => ({ ...previous, [key]: undefined }))
  }

  const back = () => navigate('/programs')

  const steps = useMemo(
    () => [
      { id: 'identity', label: S.form.steps.identity },
      { id: 'visibility', label: S.form.steps.visibility },
    ],
    [],
  )

  /* The legacy marks all four text fields required and validates nothing else. */
  const validateStep = (index) => {
    if (index > 0) return true
    const next = {}
    for (const key of ['arabicName', 'englishName', 'arabicDescription', 'englishDescription']) {
      if (!values[key].trim()) next[key] = S.form.required
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const submit = async () => {
    setSaving(true)
    /* The seven fields this form owns, and only those. `values` also holds the group
       assignment and certificate settings the row was loaded with, and posting those back
       from the NAME form would write them again — with whatever happened to be loaded. */
    const outcome = await saveProgram({
      id: values.id,
      arabicName: values.arabicName,
      arabicDescription: values.arabicDescription,
      englishName: values.englishName,
      englishDescription: values.englishDescription,
      pastCoursesView: values.pastCoursesView,
      futureCoursesView: values.futureCoursesView,
      allowAddingCoursesAfterExamination: values.allowAddingCoursesAfterExamination,
    })
    if (!outcome.ok) {
      setSaving(false)
      return toast.error(outcome.message || S.actionFailed)
    }
    toast.success(serverText(outcome.message) || S.form.saved)
    navigate('/programs')
  }

  if (loading) return <PageLoader />

  return (
    <FormPage
      icon={GraduationCap}
      title={id ? S.form.editTitle : S.actions.create}
      subtitle={S.title}
      steps={steps}
      validateStep={validateStep}
      onBack={back}
      onSubmit={submit}
      submitLabel={S.form.save}
      submitting={saving}
    >
      {({ step }) =>
        step === 0 ? (
          <FormSection>
            <FormCols>
              <FormRow label={S.form.arabicName} error={errors.arabicName}>
                {(fid) => (
                  <TextField
                    id={fid}
                    value={values.arabicName}
                    onChange={set('arabicName')}
                    invalid={!!errors.arabicName}
                    autoComplete="off"
                  />
                )}
              </FormRow>

              <FormRow label={S.form.englishName} error={errors.englishName}>
                {(fid) => (
                  <TextField
                    id={fid}
                    dir="ltr"
                    value={values.englishName}
                    onChange={set('englishName')}
                    invalid={!!errors.englishName}
                    autoComplete="off"
                  />
                )}
              </FormRow>

              <FormRow label={S.form.arabicDescription} error={errors.arabicDescription}>
                {(fid) => (
                  <Textarea
                    id={fid}
                    rows={5}
                    value={values.arabicDescription}
                    onChange={set('arabicDescription')}
                    invalid={!!errors.arabicDescription}
                  />
                )}
              </FormRow>

              <FormRow label={S.form.englishDescription} error={errors.englishDescription}>
                {(fid) => (
                  <Textarea
                    id={fid}
                    dir="ltr"
                    rows={5}
                    value={values.englishDescription}
                    onChange={set('englishDescription')}
                    invalid={!!errors.englishDescription}
                  />
                )}
              </FormRow>
            </FormCols>
          </FormSection>
        ) : (
          <FormSection>
            <FormCols>
              <FormRow label={S.form.pastCourses}>
                {(fid) => (
                  <Select
                    id={fid}
                    value={values.pastCoursesView}
                    onChange={set('pastCoursesView')}
                    options={asOptions(S.form.pastViews)}
                  />
                )}
              </FormRow>

              <FormRow label={S.form.futureCourses}>
                {(fid) => (
                  <Select
                    id={fid}
                    value={values.futureCoursesView}
                    onChange={set('futureCoursesView')}
                    options={asOptions(S.form.futureViews)}
                  />
                )}
              </FormRow>
            </FormCols>

            <SwitchField
              checked={values.allowAddingCoursesAfterExamination}
              onChange={set('allowAddingCoursesAfterExamination')}
              label={S.form.allowLateCourses}
            />

            {/* The legacy prints this banner unconditionally. It only says
                anything while the switch is on, so that is when it appears. */}
            {values.allowAddingCoursesAfterExamination && (
              <InfoNote>
                <bdi>{S.form.allowLateCoursesNote}</bdi>
              </InfoNote>
            )}
          </FormSection>
        )
      }
    </FormPage>
  )
}
